/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactNode, ReactElement } from 'react';

import semverSatisfies from 'semver/functions/satisfies';
import WebExtensionContext, { State } from './context';
import { ActionTypes, Action, SetExtensionAction, SetRequirementsAction } from './actions';

import { ExtensionStatus, Browsers } from '../../utils/constants';
import { areCookiesEnabled } from '../../utils/cookies';

const ExtensionKey = 'webExtensionVersion';

type Props = {
	children: ReactNode;
};

/* eslint-disable no-console */

function checkInstallationStatus(state: State, action: SetExtensionAction | SetRequirementsAction): ExtensionStatus {
	const { extension, requirements, useDev, status } = state;
	const { minVersion, minVersionV2 } = requirements ?? {};

	if (!extension || !minVersion) {
		return status;
	}

	const { browserName } = action.data;

	if (useDev) {
		return ExtensionStatus.Installed;
	}

	if (browserName === Browsers.Firefox && !extension?.id && minVersionV2 && !semverSatisfies(extension.version, minVersionV2)) {
		return ExtensionStatus.FirefoxV2Outdated;
	}
	return extension?.id === requirements?.id && semverSatisfies(extension?.version, minVersion)
		? ExtensionStatus.Installed
		: ExtensionStatus.Outdated;
}

function webExtensionReducer(state: State, action: Action): State {
	switch (action.type) {
		case ActionTypes.SetRequirements: {
			const newState = { ...state, requirements: action.data.requirements };
			return { ...newState, status: checkInstallationStatus(newState, action) };
		}
		case ActionTypes.SetExtension: {
			const newState = { ...state, extension: action.data };
			return { ...newState, status: checkInstallationStatus(newState, action) };
		}
		case ActionTypes.CheckTimedOut: {
			return { ...state, status: ExtensionStatus.NotInstalled };
		}
		case ActionTypes.ExtensionUpdated: {
			if (state.status === ExtensionStatus.FirefoxV2Outdated) {
				// Uninstalling Firefox V2 ends up here
				return { ...state };
			}
			return {
				...state,
				status: !state.extension?.id && state.requirements?.id ? ExtensionStatus.NotInstalled : ExtensionStatus.Installed,
			};
		}
		case ActionTypes.ExtensionUninstalled: {
			return { ...state, status: ExtensionStatus.NotInstalled };
		}
		default: {
			throw new Error(`Unhandled action type: ${action}`);
		}
	}
}
let version;
if (areCookiesEnabled()) {
	version = localStorage.getItem(ExtensionKey);
}
const initialState: State = {
	requirements: {},
	status: ExtensionStatus.Pending,
	useBeta: false,
	useDev: false,
};
if (version) {
	initialState.useBeta = version === 'beta';
	initialState.useDev = version === 'dev';
}

const ExtensionProvider = ({ children }: Props): ReactElement => {
	const [state, dispatch] = React.useReducer(webExtensionReducer, initialState);
	const context = { state, dispatch };

	return <WebExtensionContext.Provider value={context}>{children}</WebExtensionContext.Provider>;
};

export default ExtensionProvider;
